import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Loader from 'components/Loader';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import { DensityToggle, useTableDensity } from 'components/DensityToggle';
import { densityTokens } from 'theme/chart-config';
import { tableHeaderTextProps, tableProps, tableRowProps, tableThProps } from 'theme/constants';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import {
  UserHistoryItemDto,
  UserHistoryItemKind,
  UserHistoryPaymentMethod,
} from '../../../models/payment/dto/user-history.dto';
import { useExportUserHistoryCsv } from '../hooks/useExportUserHistoryCsv';
import { useGetUserHistory } from '../hooks/useGetUserHistory';
import {
  buildRowAccessibleLabel,
  formatActivityDate,
  formatBalanceAfter,
  formatCreditsAnnotation,
  formatFilterDate,
  formatImportedAtDate,
  formatOccurredAt,
  formatOccurredAtFull,
  formatSignedAmount,
  getAmountSign,
  getPaymentMethodLabel,
  getStatusBadge,
  groupItemsByDay,
  matchesSearch,
  resolveRefundOriginalPaymentDate,
} from '../utils/user-history.utils';

/** One label/value line in the row detail modal (D-A11Y "→detail"). */
function DetailRow({ label, value }: { label: string; value: string }) {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  return (
    <Flex justify="space-between" gap={4}>
      <Text fontSize="sm" color={labelColor}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="500" textAlign="right">
        {value}
      </Text>
    </Flex>
  );
}

type Props = {
  userId: string;
};

const KIND_OPTIONS: UserHistoryItemKind[] = ['payment', 'wallet', 'refund', 'activity'];
const METHOD_OPTIONS: UserHistoryPaymentMethod[] = ['wallet', 'card', 'manual', 'free', 'pay_later'];

/**
 * Unified Player History — dashboard club view (epic Phase B, T6@ceo-review +
 * T4@design-review). Placed directly below `UserWalletSection` on `UserPage`
 * (D-PLACE). Day-grouped table over `GET
 * /payments/admin/users/:userId/history`, debounced search + kind/method/
 * date filters, CSV export.
 *
 * Row spec (T5@design, dashboard half — DONE): dual dates (primary
 * `occurredAt` in the Date column, secondary muted `linkage.activityStartTime`
 * under the Item label, mirroring mobile's L1 "<label> · match du 12/08"),
 * player-perspective signs, € primary + credits annotation (dropped at
 * `walletCreditRate === 1`, D-COPY), Solde em-dash, D-BADGES mapping incl.
 * pay_later amber — all in `user-history.utils.ts`.
 *
 * Refund pairing (T6@design, dashboard half — DONE): the original-PI badge
 * (Remboursée/Remb. partiel from `item.refunds`) was already built in run 15
 * as part of the first-pass badge column; this box's one real remaining gap
 * was the refund row's own sub-line ("sur le paiement du 10/08",
 * `resolveRefundOriginalPaymentDate` in `user-history.utils.ts`) — a
 * best-effort correlation against already-loaded pages, since the DTO
 * doesn't carry the original payment's date, gated to render nothing rather
 * than a wrong/missing date when that page hasn't loaded. `coveredUserId`
 * labeling needed no client work: the payer/covered-user text is already
 * baked into `label` server-side (T6@design, api half, run 10).
 *
 * State matrix + a11y (T7@design, dashboard half — DONE): filter-empty now
 * shows a removable chip for EVERY active filter including the date range
 * (previously only kind/method had chips — a date-only filter combo landed
 * on the filter-empty branch with zero visible chips, D-STATES' "removable
 * pills" half was silently incomplete). Inline error now wires `refetch`
 * into `ErrorState`'s `onRetry` (D-STATES "inline PageCard error + retry",
 * page unaffected — the retry only re-fires the query, no navigation).
 * D-A11Y: each row gets `buildRowAccessibleLabel` (composed sentence) as its
 * `aria-label`, `tabIndex={0}` + Enter/Space to open a detail modal (D-A11Y
 * "keyboard-focusable with Enter→detail" — no detail view existed anywhere
 * for this section before this run; the modal surfaces the row's full data
 * at full precision, incl. the truncated Item label and the full activity/
 * refund-original dates the table only shows abbreviated). Day headers get
 * `role="heading"`. Deliberate deviation from `dashboard/DESIGN.md`'s
 * broader (currently unimplemented anywhere in this codebase) `role="grid"`
 * + arrow-key table-navigation pattern: this epic's own D-A11Y line asks
 * for "one row = one accessible element", a flatter pattern this row
 * satisfies with `role="button"` on the `<Tr>` — building the full grid
 * pattern from scratch for one section, while no other admin table in the
 * dashboard has it, was judged disproportionate to this box; flagged in the
 * ledger for whoever eventually tackles that systemically. Touch-target
 * sizing: the row itself is a ≥44pt target by construction (full table-row
 * height); the filter bar's `size="sm"` inputs/selects/buttons are NOT
 * resized — that is the dashboard-wide convention (same as
 * `TransactionHistoryTable`'s own filter bar), and deviating only here
 * would fight the design system rather than follow it (CLAUDE.md: don't
 * deviate from the design system without explicit approval). The literal
 * composed-sentence Item column from the epic's mockup (one prose sentence
 * instead of Date/Item/Method/Amount/Balance/Status columns) was NOT built —
 * a deliberate v1 scoping call documented in run 15, unchanged by this run.
 * D-STATES "import banner" PARTIAL-state cell (T7@design-followup — DONE):
 * `UserHistoryPageDto.importedAt` (new API surface off `User.importJob`,
 * page-1-only per its own doc comment) renders as an info `Alert` above the
 * table/empty/error states whenever it's set — an imported user's balance
 * was carried over but their ledger before that date was not, so this tells
 * them (and the operator) the history genuinely starts there. Read off
 * `data.pages[0]`, not the currently-loaded page, since it's a static
 * per-user fact that shouldn't flicker as the operator pages through — and
 * shown regardless of which filter/search/loading state is active below it,
 * since it's true independent of what the current query happens to return.
 *
 * `WalletTransactionTable` (previously rendered inside `UserWalletSection`)
 * has been removed from `UserPage` — `T8@eng`'s superset test (ADMIN_ADJUSTMENT
 * rows round-trip through this section) satisfied the D-PLACE removal gate,
 * done run 16. (Stale until this run: this paragraph previously said the
 * removal was still gated/pending.)
 */
export default function UserHistorySection({ userId }: Props) {
  const toast = useToast();
  const { density, onDensityChange } = useTableDensity('user-history');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const dayHeaderBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const dayHeaderColor = useColorModeValue('brand.700', 'brand.200');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<UserHistoryItemKind | ''>('');
  const [methodFilter, setMethodFilter] = useState<UserHistoryPaymentMethod | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailItem, setDetailItem] = useState<UserHistoryItemDto | null>(null);

  // Debounced search (epic F3 — reused pattern from TransactionHistoryTable).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const kinds = kindFilter ? [kindFilter] : undefined;
  // Day-only `<input type=date>` values widened to the full local day before
  // going to the API, which hard-validates these as ISO datetimes
  // (parse-user-history-query.ts) — a bare "2026-08-12" for dateTo would
  // exclude that entire day instead of including it.
  const isoDateFrom = dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined;
  const isoDateTo = dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetUserHistory({ userId, kinds, dateFrom: isoDateFrom, dateTo: isoDateTo });

  const { exportCsv, isExporting, isError: exportError } = useExportUserHistoryCsv(userId);

  useEffect(() => {
    if (exportError) {
      toast({
        title: translate('user_history.export_error'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [exportError, toast]);

  const allItems = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  // D-STATES PARTIAL import banner: page 1 only (see the DTO's own doc
  // comment) — `data.pages[0]`, NOT the last-loaded page, so the banner
  // doesn't disappear once the operator pages past it.
  const importedAt = data?.pages[0]?.importedAt ?? null;

  // Keyed by the namespaced item id (`pi:<id>`) across ALL loaded pages,
  // unfiltered — the refund original-payment sub-line (D-REFUND) resolves
  // against whatever's already fetched regardless of the active kind/method
  // filter, so a refund row doesn't lose its date the moment "Remboursement"
  // is selected in the kind filter and its paired PI row gets hidden.
  const itemsById = useMemo(
    () => new Map<string, UserHistoryItemDto>(allItems.map((item) => [item.id, item])),
    [allItems],
  );

  // `method` has no server-side filter (see useGetUserHistory's doc comment)
  // — filtered here on whatever pages are already loaded. `kinds`/date range
  // ARE sent to the API, so those two narrow the underlying fetch itself.
  const filteredItems = useMemo(
    () =>
      allItems.filter(
        (item) =>
          (!methodFilter || item.paymentMethod === methodFilter) &&
          matchesSearch(item, debouncedSearch),
      ),
    [allItems, methodFilter, debouncedSearch],
  );

  const dayGroups = useMemo(() => groupItemsByDay(filteredItems), [filteredItems]);

  // D-STATES filter-empty: one removable pill per ACTIVE filter, including
  // the date range (previously only kind/method produced chips, so a
  // date-only filter combo hit this branch with an empty pill row).
  //
  // Built as {label, clear} pairs rather than re-deriving which filter a chip
  // came from by string-comparing its translated label (the previous shape).
  // Each label is also prefixed with its dimension ("Type : Wallet" /
  // "Méthode : Wallet") because the bare labels genuinely collide —
  // `kind_wallet` and `method_wallet` are both "Wallet" — which would both
  // read ambiguously to an operator AND collide as React keys inside
  // `EmptyState`'s own `<Tag key={filter}>` chip list.
  const activeFilterChips: { label: string; clear: () => void }[] = [
    ...(kindFilter
      ? [
          {
            label: translate('user_history.filter_chip_kind', {
              value: translate(`user_history.kind_${kindFilter}` as any),
            }),
            clear: () => setKindFilter(''),
          },
        ]
      : []),
    ...(methodFilter
      ? [
          {
            label: translate('user_history.filter_chip_method', {
              value: translate(`user_history.method_${methodFilter}` as any),
            }),
            clear: () => setMethodFilter(''),
          },
        ]
      : []),
    ...(dateFrom
      ? [
          {
            label: translate('user_history.filter_chip_date_from', { date: formatFilterDate(dateFrom) }),
            clear: () => setDateFrom(''),
          },
        ]
      : []),
    ...(dateTo
      ? [
          {
            label: translate('user_history.filter_chip_date_to', { date: formatFilterDate(dateTo) }),
            clear: () => setDateTo(''),
          },
        ]
      : []),
  ];

  const hasActiveFilters = activeFilterChips.length > 0;

  const clearFilters = () => {
    setSearchInput('');
    setKindFilter('');
    setMethodFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleExport = () => {
    void exportCsv({ kinds, dateFrom: isoDateFrom, dateTo: isoDateTo });
  };

  return (
    <PageCard
      title={translate('user_history.title')}
      rightHeader={
        <HStack spacing={2}>
          <DensityToggle tableId="user-history" onChange={onDensityChange} />
          <Button
            size="sm"
            colorScheme="brand"
            variant="outline"
            onClick={handleExport}
            isLoading={isExporting}
            isDisabled={!allItems.length}
          >
            {translate('user_history.export_csv')}
          </Button>
        </HStack>
      }
    >
      <Flex px="25px" pb="15px" pt="10px" gap={3} flexWrap="wrap" align="center">
        {/* D-A11Y: a placeholder is not an accessible name — the two Selects
            and the search box had none (only the date inputs did), leaving a
            screen-reader user three unlabeled controls. */}
        <Input
          placeholder={translate('user_history.search_placeholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          size="sm"
          maxW="240px"
          aria-label={translate('user_history.search_placeholder')}
        />
        <Select
          placeholder={translate('user_history.all_kinds')}
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as UserHistoryItemKind | '')}
          size="sm"
          maxW="160px"
          aria-label={translate('user_history.all_kinds')}
        >
          {KIND_OPTIONS.map((kind) => (
            <option key={kind} value={kind}>
              {translate(`user_history.kind_${kind}` as any)}
            </option>
          ))}
        </Select>
        <Select
          placeholder={translate('user_history.all_methods')}
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value as UserHistoryPaymentMethod | '')}
          size="sm"
          maxW="160px"
          aria-label={translate('user_history.all_methods')}
        >
          {METHOD_OPTIONS.map((method) => (
            <option key={method} value={method}>
              {translate(`user_history.method_${method}` as any)}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          size="sm"
          maxW="160px"
          aria-label={translate('user_history.date_from')}
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          size="sm"
          maxW="160px"
          aria-label={translate('user_history.date_to')}
        />
      </Flex>

      {importedAt && (
        <Box px="25px" pb="15px">
          <Alert status="info" variant="left-accent" borderRadius="8px" fontSize="sm">
            <AlertIcon />
            <AlertDescription>
              {translate('user_history.import_banner', { date: formatImportedAtDate(importedAt) })}
            </AlertDescription>
          </Alert>
        </Box>
      )}

      <Box>
        {isLoading ? (
          // D-STATES: dashboard section loading = table skeleton.
          <Loader isLoading columns={6} />
        ) : isError ? (
          // D-STATES: inline PageCard error + retry — `refetch` only re-fires
          // this section's own query, the rest of UserPage is unaffected.
          <ErrorState message={translate('user_history.error_loading')} onRetry={() => void refetch()} />
        ) : !filteredItems.length ? (
          debouncedSearch ? (
            <EmptyState
              variant="no-results-search"
              searchQuery={debouncedSearch}
              title={translate('user_history.empty_state')}
            />
          ) : hasActiveFilters ? (
            <EmptyState
              variant="no-results-filter"
              title={translate('user_history.empty_state')}
              activeFilters={activeFilterChips.map((chip) => chip.label)}
              onRemoveFilter={(label) => {
                activeFilterChips.find((chip) => chip.label === label)?.clear();
              }}
              onClearAllFilters={clearFilters}
            />
          ) : (
            <EmptyState variant="first-use" title={translate('user_history.empty_state_first_use')} />
          )
        ) : (
          <>
            <Table {...tableProps} opacity={isFetching && !isFetchingNextPage ? 0.5 : 1} transition="opacity 0.2s">
              <Thead>
                {/* Bug fix: `tableThProps` only overrides `pe` (10px), so `ps`
                    fell through to Chakra's md-size default (24px) — an
                    asymmetric Th padding that doesn't match Td's symmetric
                    density-based `px`. Every header sat visibly off from its
                    column's data (~8-12px, both edges, every column) —
                    inherited as-is from TransactionHistoryTable, which has
                    the same latent mismatch. Pinning ps/pe to the same
                    density token Td uses is what actually fixes it. */}
                <Tr>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_date')}</Text>
                  </Th>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_item')}</Text>
                  </Th>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_method')}</Text>
                  </Th>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                    isNumeric
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_amount')}</Text>
                  </Th>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                    isNumeric
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_balance')}</Text>
                  </Th>
                  <Th
                    {...tableThProps}
                    borderColor={borderColor}
                    ps={densityTokens[density].cellPx}
                    pe={densityTokens[density].cellPx}
                  >
                    <Text {...tableHeaderTextProps}>{translate('user_history.col_status')}</Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {dayGroups.map((group) => (
                  <Fragment key={group.key}>
                    <Tr>
                      <Td
                        colSpan={6}
                        bg={dayHeaderBg}
                        borderLeft="3px solid"
                        borderColor="brand.400"
                        py={2}
                      >
                        {/* D-A11Y: day headers announced as headings. */}
                        <Text
                          role="heading"
                          aria-level={3}
                          fontSize="xs"
                          fontWeight="600"
                          color={dayHeaderColor}
                          textTransform="capitalize"
                        >
                          {group.label}
                        </Text>
                      </Td>
                    </Tr>
                    {group.items.map((item) => {
                      const sign = getAmountSign(item);
                      const badge = getStatusBadge(item);
                      const creditsAnnotation = formatCreditsAnnotation(
                        item.walletDelta,
                        item.walletCreditRate,
                      );
                      const amountColor = sign > 0 ? 'green.500' : sign < 0 ? 'red.500' : textColor;
                      const refundOriginalDate = resolveRefundOriginalPaymentDate(item, itemsById);

                      return (
                        // D-A11Y: one row = one accessible element. The
                        // composed label carries what the cells convey
                        // visually (label, signed amount + method, date,
                        // balance, status) so a screen-reader user gets the
                        // row as one sentence instead of six unlabeled cells.
                        <Tr
                          key={item.id}
                          {...tableRowProps}
                          role="button"
                          tabIndex={0}
                          aria-label={buildRowAccessibleLabel(item)}
                          onClick={() => setDetailItem(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              // Space would otherwise scroll the page.
                              e.preventDefault();
                              setDetailItem(item);
                            }
                          }}
                        >
                          <Td
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                            whiteSpace="nowrap"
                          >
                            <Tooltip label={formatOccurredAtFull(item.occurredAt)}>
                              <Text as="span">{formatOccurredAt(item.occurredAt)}</Text>
                            </Tooltip>
                          </Td>
                          <Td
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                            maxW="360px"
                          >
                            <Text color={textColor} noOfLines={1}>
                              {item.label}
                            </Text>
                            {item.linkage?.activityStartTime && (
                              <Text fontSize="xs" color={secondaryTextColor} noOfLines={1}>
                                {translate('user_history.activity_date', {
                                  date: formatActivityDate(item.linkage.activityStartTime),
                                })}
                              </Text>
                            )}
                            {refundOriginalDate && (
                              <Text fontSize="xs" color={secondaryTextColor} noOfLines={1}>
                                {translate('user_history.refund_original_date', {
                                  date: refundOriginalDate,
                                })}
                              </Text>
                            )}
                          </Td>
                          <Td
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                          >
                            {getPaymentMethodLabel(item.paymentMethod)}
                          </Td>
                          <Td
                            isNumeric
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                            fontFamily="'JetBrains Mono', monospace"
                            color={amountColor}
                            fontWeight="500"
                          >
                            {formatSignedAmount(item.amountCents, sign)}
                            {creditsAnnotation && (
                              <Text as="span" fontSize="xs" color={secondaryTextColor} ml={1}>
                                ({creditsAnnotation})
                              </Text>
                            )}
                          </Td>
                          <Td
                            isNumeric
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                            fontFamily="'JetBrains Mono', monospace"
                            color={item.walletBalanceAfter == null ? secondaryTextColor : textColor}
                          >
                            {formatBalanceAfter(item.walletBalanceAfter)}
                          </Td>
                          <Td
                            py={densityTokens[density].cellPy}
                            px={densityTokens[density].cellPx}
                            fontSize={densityTokens[density].fontSize}
                          >
                            <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Fragment>
                ))}
              </Tbody>
            </Table>

            {hasNextPage && (
              <Flex justify="center" py={4}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  isLoading={isFetchingNextPage}
                >
                  {translate('user_history.load_more')}
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* D-A11Y "Enter→detail". Shows the row at full precision: the Item
          column truncates to one line (`noOfLines={1}`) and both sub-line
          dates are abbreviated to dd/MM in the table. */}
      <Modal isOpen={Boolean(detailItem)} onClose={() => setDetailItem(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">{translate('user_history.detail_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {detailItem && (
              <VStack align="stretch" spacing={3}>
                <Text fontWeight="600">{detailItem.label}</Text>
                <Divider />
                <DetailRow
                  label={translate('user_history.col_date')}
                  value={formatOccurredAtFull(detailItem.occurredAt)}
                />
                {detailItem.linkage?.activityStartTime && (
                  <DetailRow
                    label={translate('user_history.activity_date_label')}
                    value={formatOccurredAtFull(detailItem.linkage.activityStartTime)}
                  />
                )}
                {(() => {
                  const original =
                    detailItem.kind === 'refund' && detailItem.refund?.originalPaymentIntentId
                      ? itemsById.get(`pi:${detailItem.refund.originalPaymentIntentId}`)
                      : undefined;
                  return original ? (
                    <DetailRow
                      label={translate('user_history.refund_original_date_label')}
                      value={formatOccurredAtFull(original.occurredAt)}
                    />
                  ) : null;
                })()}
                <DetailRow
                  label={translate('user_history.col_method')}
                  value={getPaymentMethodLabel(detailItem.paymentMethod)}
                />
                <DetailRow
                  label={translate('user_history.col_amount')}
                  value={[
                    formatSignedAmount(detailItem.amountCents, getAmountSign(detailItem)),
                    formatCreditsAnnotation(detailItem.walletDelta, detailItem.walletCreditRate),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <DetailRow
                  label={translate('user_history.col_balance')}
                  value={formatBalanceAfter(detailItem.walletBalanceAfter)}
                />
                <DetailRow
                  label={translate('user_history.col_status')}
                  value={getStatusBadge(detailItem).label}
                />
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageCard>
  );
}
